<template>
    <div id="account-details-view"
         class="container container--top-padding-for-mobile-header">
        <div class="two-col-page two-col-page--flip-desktop invert">
            <!-- Right col on desktop, appears first on mobile -->
            <div class="two-col-page__content">
                <!-- Account loading spinner -->
                <PageLoadingSpinner v-if="loading"
                                    color="light"
                                    lang="MANAGE_ACCOUNT.LOADING_ACCOUNT" />
                <!-- Account not found message -->
                <NoResults v-else-if="currentAccount === false"
                           icon="fa-solid fa-times-circle"
                           color="light"
                           lang="MANAGE_ACCOUNT.NOT_FOUND"></NoResults>
                <template v-else-if="currentAccount">
                    <!-- Page header for mobile -->
                    <div class="mobile-header">
                        <h2>
                            {{ l('MANAGE_ACCOUNT.ACCOUNT') }}
                            <strong>{{ currentAccount.ref }}</strong>
                        </h2>
                    </div>

                    <!-- Tabs -->
                    <Tabs :model-value="activeTabName"
                          :tabs="tabs"
                          @update:model-value="(value) => $store.commit('accountDetails/setActiveTabName', value)" />

                    <!-- Active tab content -->
                    <AccountDetailsMembershipsTab v-if="activeTabName === AccountDetailsTabNameEnum.MEMBERSHIPS"
                                                  :account="currentAccount" />
                    <AccountDetailsContactsTab v-else-if="activeTabName === AccountDetailsTabNameEnum.CONTACTS"
                                               :account="currentAccount" />
                    <AccountDetailsOrdersTab v-else-if="activeTabName === AccountDetailsTabNameEnum.ORDERS"
                                             :account="currentAccount" />
                    <AccountDetailsAddressesTab v-else-if="activeTabName === AccountDetailsTabNameEnum.ADDRESSES"
                                                :account="currentAccount" />
                </template>
            </div>

            <!-- Left col on desktop, appears at bottom on mobile -->
            <div class="two-col-page__sidebar">
                <div class="page-action-btns desktop-only">
                    <!-- Back button (desktop only) -->
                    <BackBtn />

                    <!-- Refresh order button -->
                    <SquareBtn v-if="currentAccount"
                               size="lg"
                               :loading="loading"
                               @click="refreshAccount ">
                        <i class="fa-regular fa-arrows-rotate"></i>
                        {{ l('REFRESH') }}
                    </SquareBtn>
                </div>

                <!-- Account details column -->
                <AccountDetailsDetailsCol v-if="currentAccount"
                                          :account="currentAccount" />
            </div>
        </div>
    </div>
</template>

<script>
import AccountDetailsAddressesTab from '@/epos/vue/pages/account-details/AccountDetailsAddressesTab';
import AccountDetailsContactsTab from '@/epos/vue/pages/account-details/AccountDetailsContactsTab';
import AccountDetailsDetailsCol from '@/epos/vue/pages/account-details/components/AccountDetailsDetailsCol';
import AccountDetailsMembershipsTab from '@/epos/vue/pages/account-details/AccountDetailsMembershipsTab';
import AccountDetailsOrdersTab from '@/epos/vue/pages/account-details/AccountDetailsOrdersTab';
import BackBtn from '@/vue/components/BackBtn';
import NoResults from '@/vue/components/NoResults';
import NotFoundApiError from '@/lib/Api/NotFoundApiError';
import OrderSearchCriteria from '@/lib/Orders/Search/OrderSearchCriteria';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import SquareBtn from '@/vue/components/SquareBtn';
import Tabs from '@/vue/components/Tabs/Tabs';
import { AccountDetailsTabNameEnum } from '@/epos/vue/pages/account-details/AccountDetailsTabNameEnum';
import { container } from '@/container';
import { mapState } from 'vuex';

export default {
    components: {
        NoResults,
        SquareBtn,
        PageLoadingSpinner,
        BackBtn,
        AccountDetailsAddressesTab,
        AccountDetailsOrdersTab,
        AccountDetailsContactsTab,
        AccountDetailsMembershipsTab,
        Tabs,
        AccountDetailsDetailsCol,
    },

    data() {
        return {
            /**
             * Tab names are used in the template.
             */
            AccountDetailsTabNameEnum,

            loading: false,
        };
    },

    computed: {
        ...mapState('accountDetails', ['currentAccount', 'activeTabName']),

        tabs() {
            return [
                {
                    name: AccountDetailsTabNameEnum.MEMBERSHIPS,
                    label: 'MANAGE_ACCOUNT.MEMBERSHIPS_TAB',
                    badge: this.currentAccount?.memberships.length,
                    icon: 'fa-solid fa-users',
                },
                {
                    name: AccountDetailsTabNameEnum.CONTACTS,
                    label: 'MANAGE_ACCOUNT.CONTACTS_TAB',
                    badge: this.currentAccount?.contacts.length,
                    icon: 'fa-solid fa-address-card',
                },
                {
                    name: AccountDetailsTabNameEnum.ORDERS,
                    label: 'MANAGE_ACCOUNT.ORDERS_TAB',
                    badge: this.$store.state.accountDetails.currentAccountOrderSearchResults?.orders?.length,
                    icon: 'fa-solid fa-shopping-cart',
                },
                {
                    name: AccountDetailsTabNameEnum.ADDRESSES,
                    label: 'MANAGE_ACCOUNT.ADDRESSES_TAB',
                    badge: this.currentAccount?.addresses.length,
                    icon: 'fa-solid fa-location-dot',
                },
            ];
        },

        pathAccountID() {
            // The path looks like : /accounts/5
            // Extract the accountID from the path.
            // Note we are using Vue Router here!
            const path = this.$route.path;
            const parts = path.split('/');
            return parseInt(parts[2]);
        },
    },

    watch: {
        pathAccountID: {
            handler(accountID) {
                if (!accountID) {
                    // Originally this went back to the account search page if there was no accountID.
                    // But that breaks when you unload the page because the URL changes then.
                    // So just do nothing.
                    return;
                }

                const forceRefresh = container.$location.search().r;

                if (forceRefresh || !this.currentAccount || this.currentAccount.ID !== accountID) {
                    // Load the account details for the (changed) account in the URL.
                    this.loadAccount(accountID);
                }
            },
            immediate: true,
        },
    },

    async created() {
        container.navigationService.showNav();

        // Check user has permission to view this page.
        await container.privilegesManager.requirePrivilegeOr403(PrivilegeNames.ACCESS_ACCOUNTS);
    },

    methods: {
        refreshAccount() {
            if (this.currentAccount) {
                this.loadAccount(this.currentAccount.ID);
            }
        },

        /**
         * @param {number} accountID
         *
         * @returns {Promise}
         */
        async loadAccount(accountID) {
            await this.$store.dispatch('accountDetails/clear');

            this.loading = true;

            try {
                const account = await container.customerAccountService.get(accountID);
                this.$store.commit('accountDetails/setCurrentAccount', account);

                this.loadOrders(account.ID);
            } catch (e) {
                if (e instanceof NotFoundApiError) {
                    // Store false in the store to indicate that the account was not found.
                    this.$store.commit('accountDetails/setCurrentAccount', false);
                }
                // Otherwise (because this uses angular-resource) the error will be handled by the global error
                // handler and displayed to the user.
            } finally {
                this.loading = false;
            }
        },

        loadOrders(customerAccountID) {
            this.$store.commit('accountDetails/setOrdersLoading', true);

            const searchCriteria = new OrderSearchCriteria();
            searchCriteria.customerAccountID = customerAccountID;
            searchCriteria.includeCancelled = true;

            container.orderSearcher
                .search(searchCriteria)
                .then(
                    /**
                     * @param {OrderSearchResults} results
                     */
                    (results) => {
                        this.$store.commit('accountDetails/setCurrentAccountOrderSearchResults', results);
                    }
                )
                .finally(() => {
                    this.$store.commit('accountDetails/setOrdersLoading', false);
                });
        },
    },
};
</script>
